/**
 * Standard set of error codes used throughout the APIs in this
 * application. Makes troubleshooting from the logs easier.
 */
export const StandardErrorCodes = {
  DatabaseProblem: 1,
  NotFound: 2,
  SomethingWentWrong: 3,
  InvalidData: 4,
  BEConfigError: 5,
  DBConnectionError: 6,
  CORSError: 7,
  UserInfoExists: 8,
  SystemError: 9,
  ErrorCreatingUser: 10,
  LoginError: 11,
  UnauthorizedUser: 12,
  UserUpdate: 13,
  InvalidSettings: 14,
  NonAdmin: 15,
  InvalidPassage: 16,
  HealthCheck: 17,
  InvalidPlanStatus: 18,
  PlanVersion: 19,
  InvalidPlan: 20,
  InvalidDaysForPlan: 21,
  IPForPublishedPlan: 22,
  LogoutError: 23,
  NoteSearchError: 24,
  NonPrayerListError: 25,
  InsertItem: 26,
  DeleteItem: 27,
  UpdateItem: 28,
  InvalidOsisForBounds: 29,
  InvalidOsisForVerses: 30,
  EmailSend: 31,
  NoRegisteredUser: 32,
  InvalidGraphData: 33,
  InvalidGraphEntriesLength: 34,
  InvalidSetting: 35,
  DuplicateServerMessageLang: 36,
  InvalidBibleApiKey: 37,
  InvalidBibleId: 38,
  InvalidBiblePassage: 39,
  APIError: 40,
  AlreadyVoted: 41,
  FeedbackArchived: 42,
  PayloadTooLarge: 43,
  PlanConflict: 44,
} as const;

// prettier-ignore
export type StandardErrorCodes = typeof StandardErrorCodes[keyof typeof StandardErrorCodes]

/**
 * Interface used for JSON error responses from APIs
 */
export interface ErrorResponse {
  errorCode: StandardErrorCodes;
  errors: {
    message: string;
    field?: string;
  }[];
}

/**
 * Standard type used for errors thrown from APIs. Implemented
 * as an **abstract** class, with the intent that specific
 * exceptions will extend this class and implement the
 * {@link serializeErrors} and {@link serializeInternalError}
 * methods with case-specific implementations.
 */
export abstract class CustomError extends Error {
  abstract statusCode: number;
  abstract errorCode: StandardErrorCodes;

  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, CustomError.prototype);
  }

  /**
   * Called to get an {@link ErrorResponse} object with details
   * about the exception, and a list of 0 or more (almost always
   * at least 1) specific errors being raised.
   *
   * @returns An {@link ErrorResponse} object with details about the error
   */
  abstract serializeErrors(): ErrorResponse;

  /**
   * Gets details about the error in string form, typically for
   * logging purposes. Not called directly by outside code,
   * the {@link serializeErrorsToString} function is better
   * for that, since it returns the error code along with the
   * response from this function (hence the "internal" in the
   * name).
   *
   * @returns String representation of the error
   */
  abstract serializeInternalError(): string;

  /**
   * Returns a string representation of the exception. On a case-
   * by-case basis specific exceptions can decide what that means.
   *
   * @returns A string representing the exception
   */
  serializeErrorsToString(): string {
    return `Error code ${this.errorCode}. ` + this.serializeInternalError();
  }
}
